import { useCallback, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/store/auth'
import { Badge, Card, Constellation, Spinner, type ConstellationNode } from '@diwata/ironvale'
import { buildKeeperRoster, TIER_HUE, type RosterGroup } from './familiarRoster'

// The keeper's home surface: the whole household drawn as one living sky, with the card grid as
// its index. Relationships ARE the content — the Sovereign at centre, Adepts by delegation,
// bonded peers in the outer orbit — rendered on the shared ironvale Constellation primitive.

type FamiliarTier = 'sovereign' | 'adept' | 'guild'

function tierVariant(tier: string): FamiliarTier {
  if (tier === 'sovereign' || tier === 'adept' || tier === 'guild') return tier
  return 'guild'
}

function statusVariant(status: string) {
  if (status === 'active') return 'success' as const
  if (status === 'suspended') return 'error' as const
  return 'neutral' as const
}

const SKY_THEME = {
  ring: '#c9a96e', // gold constellation lines
  star: '#e8e2d9', // parchment star core
  starHalo: '#7a7268', // ash default halo
  focusCore: '#e0564a', // the centre wears the ego crimson
  focusHalo: '#f48a7f',
  label: '#e8e2d9',
  sector: '#7a7268',
}

function getGroupColor(group: string): string | undefined {
  return TIER_HUE[group as RosterGroup]
}

export function FamiliarList() {
  const api = useAuth((s) => s.api)()
  const navigate = useNavigate()

  const { data: familiars = [], isLoading, error } = useQuery({
    queryKey: ['familiars'],
    queryFn: () => api.listFamiliars(),
  })

  // Bonds are fetched for the centre only — the home sky is the household seen from the Sovereign.
  const centerId = useMemo(
    () => (familiars.find((f) => f.tier === 'sovereign') ?? familiars[0])?.id ?? null,
    [familiars],
  )
  const { data: bonds = [] } = useQuery({
    queryKey: ['bonds', centerId],
    queryFn: () => api.listBonds(centerId!),
    enabled: !!centerId,
  })

  const roster = useMemo(() => buildKeeperRoster({ familiars, bonds }), [familiars, bonds])

  const ownedIds = useMemo(() => new Set(familiars.map((f) => f.id)), [familiars])
  const onNodeClick = useCallback(
    (node: ConstellationNode) => {
      if (ownedIds.has(node.id)) navigate(`/familiar/${node.id}`)
    },
    [ownedIds, navigate],
  )

  return (
    <div>
      <div className="cp-page-header">
        <div>
          <div className="cp-page-title">Familiars</div>
          <div className="cp-page-subtitle">
            Your household as one sky:{' '}
            <span style={{ color: TIER_HUE.adept }}>Adepts</span> by delegation,{' '}
            <span style={{ color: TIER_HUE.bond }}>bonds</span> in the outer orbit.
          </div>
        </div>
        <Link to="/create" className="iv-button iv-button--primary iv-button--sm">+ Create familiar</Link>
      </div>

      {isLoading && <Spinner style={{ display: 'block', margin: '24px auto' }} />}
      {error && <p className="cp-error">{String(error)}</p>}

      {!isLoading && familiars.length === 0 && (
        <Card style={{ textAlign: 'center', padding: '48px 24px' }}>
          <p style={{ fontWeight: 600, marginBottom: 8 }}>No familiars yet</p>
          <p className="cp-page-subtitle" style={{ marginBottom: 20 }}>
            Create your first familiar to get started.
          </p>
          <Link to="/create" className="iv-button iv-button--primary iv-button--sm">Create familiar</Link>
        </Card>
      )}

      {!isLoading && familiars.length > 0 && (
        <div className="cp-constellation-shell" style={{ marginBottom: 'var(--ae-space-5)' }}>
          <Constellation
            nodes={roster.nodes}
            edges={roster.edges}
            egoId={roster.centerId}
            focusId={roster.centerId}
            theme={SKY_THEME}
            getGroupColor={getGroupColor}
            onNodeClick={onNodeClick}
            timeControl={false}
          >
            {roster.nodes.length === 1 && (
              <div className="cp-sky-hint">
                <p className="cp-sky-hint-title">A constellation of one, for now</p>
                <p className="cp-sky-hint-copy">
                  Create an Adept or form a Bond and it joins this sky as a new star.
                </p>
              </div>
            )}
          </Constellation>
        </div>
      )}

      <div className="cp-card-grid">
        {familiars.map((f) => (
          <Link key={f.id} to={`/familiar/${f.id}`} className="cp-familiar-card">
            <div className="cp-familiar-name">{f.name}</div>
            <div className="cp-familiar-meta">
              <Badge variant={tierVariant(f.tier)}>{f.tier}</Badge>
              <Badge variant={statusVariant(f.status)}>{f.status}</Badge>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}
